import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { ReportPreviewPayload } from 'api/analytics-reports/types';
import { BrandLogo } from '@kontentino/ui';
import ANALYTICS_REPORT_COVER_IMAGE from 'app/modules/analytics/modules/reports/assets/analytics-report-cover-image.svg';
import {
  getReportPreviewExportCoverStyle,
  reportPreviewExportCoverClassName,
  reportPreviewPageShellClassName,
  type ReportPreviewLayout,
} from 'app/modules/analytics/modules/reports/constants/reportPreviewPageShellClassName';
import { formatReportPreviewDateRange } from 'app/modules/analytics/modules/reports/utils/formatReportPreviewDateRange';
import { resolveReportPreviewProfileHeaderMeta } from 'app/modules/analytics/modules/reports/utils/resolveReportPreviewProfileHeaderMeta';
import { OVERVIEW_REPORT_TEMPLATE_CODE } from 'app/modules/analytics/modules/reports/constants/quickReportDefaults';
import getSocialMediaTypeForBrandLogo from 'app/utils/getSocialMediaTypeForBrandLogo';
import { getYtLogoGuidelineSize } from 'app/utils/getytlogoguidelinesize';

interface Props {
  preview: ReportPreviewPayload;
  layout?: ReportPreviewLayout;
  totalPages: number;
}

const AnalyticsReportCoverPage = ({
  preview,
  layout = 'export',
  totalPages,
}: Props) => {
  const { t } = useTranslation();
  const { report, profiles } = preview;
  const since = report.period.from;
  const until = report.period.to;
  const rangeLabel =
    since && until
      ? formatReportPreviewDateRange(dayjs(since), dayjs(until))
      : '';

  const sortedProfiles = [...profiles].sort(
    (left, right) => left.order_column - right.order_column,
  );

  const coverClassName =
    layout === 'export'
      ? reportPreviewExportCoverClassName
      : reportPreviewPageShellClassName;

  return (
    <article
      className={coverClassName}
      data-cy="analytics_report-public_cover-page"
      style={
        layout === 'export' ? getReportPreviewExportCoverStyle() : undefined
      }
    >
      <div className="tw-flex tw-w-full tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-p-12">
        <img
          className="tw-mb-8 tw-h-[180px] tw-w-[180px] tw-object-contain"
          src={ANALYTICS_REPORT_COVER_IMAGE}
          alt={t('analyticsReports.cover.altText')}
        />
        <div className="tw-mb-2 tw-max-w-[750px] tw-break-words tw-text-center tw-text-3xl tw-font-semibold">
          {report.name}
        </div>
        {rangeLabel ? (
          <span className="tw-block tw-text-center tw-text-xl">
            {rangeLabel}
          </span>
        ) : null}
        {report.template_code === OVERVIEW_REPORT_TEMPLATE_CODE ? (
          <span className="tw-mt-2 tw-block tw-text-center tw-text-sm tw-font-medium tw-text-grayscale-100">
            {t('analyticsReports.overviewReportLabel', {
              defaultValue: 'Overview',
            })}
          </span>
        ) : null}
      </div>
      {sortedProfiles.length > 0 ? (
        <div className="tw-flex tw-flex-wrap tw-gap-6 tw-px-12 tw-pb-8">
          {sortedProfiles.map((profile) => {
            const meta = resolveReportPreviewProfileHeaderMeta(profile, t);
            const socialMediaType =
              meta.pageType != null
                ? getSocialMediaTypeForBrandLogo(meta.pageType)
                : undefined;

            return (
              <div
                key={profile.id}
                className="tw-flex tw-items-center tw-gap-4"
              >
                {meta.pageType != null ? (
                  <BrandLogo
                    size={70}
                    iconSize={getYtLogoGuidelineSize(24, meta.pageType)}
                    name={meta.label}
                    src={meta.logoSrc}
                    socialMediaType={socialMediaType}
                  />
                ) : null}
                <div className="tw-flex tw-flex-col">
                  <span
                    translate="no"
                    className="tw-text-xl tw-font-semibold tw-text-grayscale-180"
                  >
                    {meta.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : null}
      <footer className="tw-mx-12 tw-flex tw-items-center tw-justify-between tw-border-t tw-border-grayscale-20 tw-py-4">
        <span className="tw-text-xs tw-font-medium tw-text-grayscale-180">
          {t('analyticsReports.pagination', {
            current: 1,
            total: totalPages,
          })}
        </span>
        <p className="tw-text-xs tw-text-grayscale-100">
          {t('analyticsReports.createReport.previewGeneratedBy')}{' '}
          <a
            href="https://kontentino.com"
            target="_blank"
            rel="noopener noreferrer"
            className="tw-underline"
          >
            Kontentino
          </a>
        </p>
      </footer>
    </article>
  );
};

export default AnalyticsReportCoverPage;
